import {
  AuiIf,
  ThreadListItemMorePrimitive,
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAui,
  useAuiState,
} from '@assistant-ui/react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '@iq/ui';
import {
  // ArchiveIcon, // archive feature disabled — only delete is supported
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from 'lucide-react';
import { type FC, type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ThreadList: FC = () => {
  return (
    <ThreadListPrimitive.Root className="aui-root aui-thread-list-root flex flex-col gap-1">
      <ThreadListNew />
      <AuiIf condition={(s) => s.threads.isLoading}>
        <ThreadListSkeleton />
      </AuiIf>
      <AuiIf condition={(s) => !s.threads.isLoading}>
        <ThreadListPrimitive.Items>{() => <ThreadListItem />}</ThreadListPrimitive.Items>
      </AuiIf>
    </ThreadListPrimitive.Root>
  );
};

const ThreadListNew: FC = () => {
  const { t } = useTranslation();
  return (
    <ThreadListPrimitive.New asChild>
      <Button
        variant="outline"
        className="aui-thread-list-new h-9 justify-start gap-2 rounded-lg px-3 text-sm hover:bg-muted data-active:bg-muted"
      >
        <PlusIcon className="size-4" />
        {t('chat.sidebar.newThread')}
      </Button>
    </ThreadListPrimitive.New>
  );
};

const ThreadListSkeleton: FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          role="status"
          aria-label={t('chat.sidebar.loadingThreads')}
          className="aui-thread-list-skeleton-wrapper flex h-9 items-center px-3"
        >
          <Skeleton className="aui-thread-list-skeleton h-4 w-full" />
        </div>
      ))}
    </div>
  );
};

const ThreadListItem: FC = () => {
  const { t } = useTranslation();
  // Rename dialog open-state lives on the row (a sibling of the dropdown, not
  // nested inside it) so the menu can fully close before the modal mounts.
  const [renameOpen, setRenameOpen] = useState(false);
  return (
    <ThreadListItemPrimitive.Root className="aui-thread-list-item group flex h-9 items-center gap-2 rounded-lg transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none data-active:bg-muted">
      <ThreadListItemPrimitive.Trigger className="aui-thread-list-item-trigger flex h-full min-w-0 flex-1 items-center px-3 text-start text-sm">
        <span className="aui-thread-list-item-title min-w-0 flex-1 truncate">
          <ThreadListItemPrimitive.Title fallback={t('chat.sidebar.newThreadTitle')} />
        </span>
      </ThreadListItemPrimitive.Trigger>
      <ThreadListItemMore onRename={() => setRenameOpen(true)} />
      <RenameThreadDialog open={renameOpen} onOpenChange={setRenameOpen} />
    </ThreadListItemPrimitive.Root>
  );
};

const ThreadListItemMore: FC<{ onRename: () => void }> = ({ onRename }) => {
  const { t } = useTranslation();
  return (
    <ThreadListItemMorePrimitive.Root>
      <ThreadListItemMorePrimitive.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="aui-thread-list-item-more me-2 size-7 p-0 opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:bg-accent data-[state=open]:opacity-100 group-data-active:opacity-100"
        >
          <MoreHorizontalIcon className="size-4" />
          <span className="sr-only">{t('chat.actions.moreOptions')}</span>
        </Button>
      </ThreadListItemMorePrimitive.Trigger>
      <ThreadListItemMorePrimitive.Content
        side="bottom"
        align="start"
        className="aui-thread-list-item-more-content z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
      >
        <ThreadListItemMorePrimitive.Item
          className="aui-thread-list-item-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
          data-testid="thread-rename-trigger"
          // Defer to the next tick so the dropdown finishes its close (focus
          // return + body pointer-events cleanup) before the modal dialog
          // mounts; otherwise the dialog can flash shut.
          onSelect={() => setTimeout(onRename, 0)}
        >
          <PencilIcon className="size-4" />
          {t('chat.sidebar.renameThread')}
        </ThreadListItemMorePrimitive.Item>
        {/* Archive action intentionally disabled — only rename + delete exposed. */}
        <ThreadListItemPrimitive.Delete asChild>
          <ThreadListItemMorePrimitive.Item className="aui-thread-list-item-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-destructive text-sm outline-none hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive">
            <TrashIcon className="size-4" />
            {t('chat.sidebar.deleteThread')}
          </ThreadListItemMorePrimitive.Item>
        </ThreadListItemPrimitive.Delete>
      </ThreadListItemMorePrimitive.Content>
    </ThreadListItemMorePrimitive.Root>
  );
};

/**
 * Rename dialog for a single thread. Rendered inside `ThreadListItemPrimitive.Root`
 * so the assistant-ui thread-list-item context is available — it reads the
 * current title for the prefill and calls `rename()` on the runtime, which the
 * `thread-list-adapter` maps onto `PATCH /threads/:id` + a sidebar reload.
 * assistant-ui has no `Rename` primitive, so this is driven manually.
 */
const RenameThreadDialog: FC<{ open: boolean; onOpenChange: (open: boolean) => void }> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const aui = useAui();
  const currentTitle = useAuiState((s) => s.threadListItem.title);
  const [value, setValue] = useState('');

  // Seed (and re-seed) the input with the live title every time the dialog
  // opens, not just on mount — the same row component is reused as the user
  // navigates the list.
  useEffect(() => {
    if (open) setValue(currentTitle ?? '');
  }, [open, currentTitle]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = value.trim();
    if (!next) return;
    void aui.threadListItem().rename(next);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-md rounded-2xl"
        data-testid="thread-rename-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('chat.sidebar.renameThread')}</DialogTitle>
          <DialogDescription>{t('chat.sidebar.renameDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('chat.sidebar.renamePlaceholder')}
            aria-label={t('chat.sidebar.renameThread')}
            maxLength={200}
            data-testid="thread-rename-input"
          />
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('chat.sidebar.renameCancel')}
            </Button>
            <Button type="submit" disabled={!value.trim()} data-testid="thread-rename-save">
              {t('chat.sidebar.renameSave')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
